import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Save, Bold, Italic, List, ListOrdered, Link2, Heading2, Undo, Redo, Strikethrough, Sparkles, Loader2 } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useAiSuggestions } from '../hooks/useAiSuggestions';
import { useAiNormalize } from '../hooks/useAiNormalize';
import AiSuggestionChips from '../components/AiSuggestionChips';
import AINormalizationPanel from '../components/AINormalizationPanel';
import { VisibilitySelector } from '../components/VisibilitySelector';

const CATEGORIES = [
  { id: 'financieel', name: 'Financieel & Belasting' },
  { id: 'contracten', name: 'Contracten & Huur' },
  { id: 'leveranciers', name: 'Leveranciers & Inkoop' },
  { id: 'techniek', name: 'Techniek & Apparatuur' },
  { id: 'voertuigen', name: 'Voertuigen' },
  { id: 'vastgoed', name: 'Vastgoed & Facilitair' },
  { id: 'veiligheid', name: 'Veiligheid & Compliance' },
  { id: 'milieu', name: 'Milieu & Infrastructuur' },
  { id: 'nieuwbouw', name: 'Nieuwbouw & Verbouwing' },
  { id: 'overnames', name: 'Overnames' },
  { id: 'automatisering', name: 'Automatisering' },
];

function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: 'Schrijf hier de inhoud...' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when value changes externally (e.g., loading for edit)
  useEffect(() => {
    if (editor && value && editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL invoeren:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        isActive ? 'bg-[#2E75B6] text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-[#21262D] hover:text-slate-700 dark:hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-slate-200 dark:border-[#30363D] rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 dark:border-[#30363D] bg-slate-50/50 dark:bg-[#161B22]">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Koptekst"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Vet"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Cursief"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Doorhalen"
        >
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Opsomming"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Genummerde lijst"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton
          onClick={addLink}
          isActive={editor.isActive('link')}
          title="Link"
        >
          <Link2 className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          isActive={false}
          title="Ongedaan maken"
        >
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          isActive={false}
          title="Opnieuw"
        >
          <Redo className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Editor */}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-3 py-3 min-h-[180px] focus-within:ring-2 focus-within:ring-[#2E75B6]/20 text-slate-700 dark:text-[#E6EDF3] dark:bg-[#0D1117] [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[160px] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-slate-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none"
      />
    </div>
  );
}

export default function EntryForm() {
  const { entryId } = useParams();
  const isEditing = !!entryId;
  const navigate = useNavigate();
  const { authAxios, user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const { suggestions, loading: aiLoading, dismissed: aiDismissed, debouncedFetch, dismiss: aiDismiss, reset: aiReset } = useAiSuggestions();
  const { result: normalizeResult, loading: normalizeLoading, error: normalizeError, normalize, reset: normalizeReset } = useAiNormalize();
  const [normalizeTriggered, setNormalizeTriggered] = useState(false);
  const [form, setForm] = useState({
    location_id: '',
    bv_id: '',
    category: '',
    subcategory: '',
    title: '',
    content: '',
    tags: '',
    entry_status: 'active',
    visibility: 'normal',
    visibility_granted_users: [],
  });

  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const locRes = await api.get('/locations');
        setLocations(locRes.data);
        // Load users for visibility picker
        try {
          const usersRes = await api.get('/users/list');
          setAllUsers(usersRes.data);
        } catch {}

        if (isEditing) {
          const entryRes = await api.get(`/entries/${entryId}`);
          const e = entryRes.data;
          setForm({
            location_id: e.location_id || '',
            bv_id: e.bv_id || '',
            category: e.category || '',
            subcategory: e.subcategory || '',
            title: e.title || '',
            content: e.content || '',
            tags: e.tags || '',
            entry_status: e.entry_status || 'active',
            visibility: e.visibility || 'normal',
            visibility_granted_users: e.visibility_granted_users || [],
          });
        }
      } catch (e) {
        toast.error('Fout bij laden');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authAxios, entryId, isEditing]);

  // Auto-fill bv_id when location changes
  useEffect(() => {
    if (form.location_id && form.location_id !== 'all') {
      const loc = locations.find(l => l.location_id === form.location_id);
      if (loc) {
        setForm(prev => ({ ...prev, bv_id: loc.bv_id }));
      }
    }
  }, [form.location_id, locations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location_id || !form.title) {
      toast.error('Locatie en titel zijn verplicht');
      return;
    }
    setSaving(true);
    try {
      const api = authAxios();
      const payload = {
        ...form,
        ai_categorized: aiApplied,
        ai_normalized: normalizeTriggered && normalizeResult?.success,
        ai_confidence: normalizeResult?.category_confidence || null,
        original_input: normalizeTriggered ? (normalizeResult ? JSON.stringify({ title: form.title, content: form.content }) : null) : null,
        ai_normalize_suggestions: normalizeResult?.suggestions?.map((text, i) => ({
          id: `sug-${Date.now()}-${i}`,
          text,
          type: 'action',
          priority: 'medium',
          status: 'pending',
        })) || null,
        key_data: normalizeResult?.key_data || null,
      };
      if (isEditing) {
        await api.put(`/entries/${entryId}`, payload);
        toast.success('Item bijgewerkt');
        navigate(`/kennisbank/${entryId}`);
      } else {
        const res = await api.post('/entries', payload);
        toast.success('Item aangemaakt');
        navigate(`/kennisbank/${res.data.entry_id}`);
      }
    } catch (e) {
      toast.error('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm(prev => ({ ...prev, title }));
    if (!isEditing) debouncedFetch(title, form.content);
  };

  const handleApplySuggestion = (type, value) => {
    if (type === 'category') {
      setForm(prev => ({ ...prev, category: value }));
      setAiApplied(true);
      toast.success('Categorie ingevuld door AI');
    } else if (type === 'location') {
      setForm(prev => ({ ...prev, location_id: value }));
      setAiApplied(true);
      toast.success('Locatie ingevuld door AI');
    } else if (type === 'tag') {
      setForm(prev => {
        const existing = prev.tags ? prev.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
        if (!existing.includes(value)) existing.push(value);
        return { ...prev, tags: existing.join(', ') };
      });
      setAiApplied(true);
      toast.success(`Tag "${value}" toegevoegd`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[800px] animate-pulse">
        <div className="h-8 w-48 bg-slate-100 dark:bg-[#21262D] rounded mb-6" />
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#30363D] p-6 space-y-4">
          {[1,2,3,4].map(i => <div key={i} className="h-10 bg-slate-100 dark:bg-[#21262D] rounded" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[800px]">
      <button
        onClick={() => navigate(isEditing ? `/kennisbank/${entryId}` : '/kennisbank')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#2E75B6] mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {isEditing ? 'Terug naar item' : 'Terug naar Kennisbank'}
      </button>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#30363D] p-6 space-y-5">
          <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-[#E6EDF3]">
            {isEditing ? 'Item Bewerken' : 'Nieuw Item'}
          </h2>

          {/* Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Locatie *</label>
              <select
                data-testid="entry-form-location"
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
                required
              >
                <option value="">Selecteer locatie...</option>
                <option value="all">Alle locaties (groepsbreed)</option>
                {locations.map(loc => (
                  <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Bedrijfsdomein</label>
              <select
                data-testid="entry-form-bv"
                value={form.bv_id}
                onChange={(e) => setForm({ ...form, bv_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
              >
                <option value="">Selecteer...</option>
                <option value="truckwash">Truckwash</option>
                <option value="truckshop">Truckshop 1</option>
                <option value="truckstop">Truckstop 8</option>
                <option value="truckssupply">Truckssupply</option>
                <option value="techniek">Techniek & Beheer</option>
                <option value="holding">MT / Holding</option>
              </select>
            </div>
          </div>

          {/* Category + Subcategory */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Categorie *</label>
              <select
                data-testid="entry-form-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
              >
                <option value="">Selecteer categorie...</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Subcategorie</label>
              <input
                data-testid="entry-form-subcategory"
                type="text"
                value={form.subcategory}
                onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                placeholder="Optioneel..."
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Titel *</label>
            <input
              data-testid="entry-form-title"
              type="text"
              value={form.title}
              onChange={handleTitleChange}
              placeholder="Korte beschrijving..."
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
              required
            />
            {!isEditing && (
              <AiSuggestionChips
                suggestions={suggestions}
                loading={aiLoading}
                onApply={handleApplySuggestion}
                onDismiss={aiDismiss}
              />
            )}
          </div>

          {/* Content - Rich text */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Inhoud</label>
            <RichTextEditor
              value={form.content}
              onChange={(value) => setForm({ ...form, content: value })}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Tags</label>
            <input
              data-testid="entry-form-tags"
              type="text"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="Komma-gescheiden tags, bijv: contract, huur, 2024"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
            />
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">Gescheiden door komma's voor zoekfunctionaliteit</p>
          </div>

          {/* AI Normalize Button + Panel */}
          {!isEditing && form.title && form.title.length >= 3 && (
            <div className="space-y-3">
              {!normalizeResult && !normalizeLoading && (
                <button
                  type="button"
                  data-testid="ai-normalize-trigger"
                  onClick={() => {
                    setNormalizeTriggered(true);
                    normalize(form.title, form.content);
                  }}
                  disabled={normalizeLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#2E75B6]/30 bg-[#2E75B6]/[0.03] text-[#2E75B6] text-xs font-medium hover:bg-[#2E75B6]/10 transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  AI Normalisatie
                </button>
              )}

              <AINormalizationPanel
                result={normalizeResult}
                loading={normalizeLoading}
                error={normalizeError}
                originalTitle={form.title}
                originalContent={form.content}
                onApplyAll={(res) => {
                  setForm(prev => ({
                    ...prev,
                    title: res.cleaned_title || prev.title,
                    content: res.cleaned_content || prev.content,
                    category: res.category || prev.category,
                    location_id: res.location || prev.location_id,
                    tags: res.tags?.length ? res.tags.join(', ') : prev.tags,
                  }));
                  setAiApplied(true);
                  toast.success('AI suggesties overgenomen');
                }}
                onApplyField={(field, value) => {
                  if (field === 'title') {
                    setForm(prev => ({ ...prev, title: value }));
                  } else if (field === 'content') {
                    setForm(prev => ({ ...prev, content: value }));
                  } else if (field === 'category') {
                    setForm(prev => ({ ...prev, category: value }));
                  } else if (field === 'location') {
                    setForm(prev => ({ ...prev, location_id: value }));
                  } else if (field === 'tag') {
                    setForm(prev => {
                      const existing = prev.tags ? prev.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
                      if (!existing.includes(value)) existing.push(value);
                      return { ...prev, tags: existing.join(', ') };
                    });
                  }
                  setAiApplied(true);
                  toast.success(`${field === 'tag' ? 'Tag' : field.charAt(0).toUpperCase() + field.slice(1)} overgenomen`);
                }}
                onAcceptCompliance={(item) => {
                  toast.success(`Compliance-item "${item.title}" genoteerd — maak het aan na opslaan`);
                }}
                onDismiss={() => {
                  normalizeReset();
                  setNormalizeTriggered(false);
                }}
                onRetry={() => normalize(form.title, form.content)}
              />
            </div>
          )}

          {/* Status */}
          {isEditing && (
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Status</label>
              <select
                data-testid="entry-form-status"
                value={form.entry_status}
                onChange={(e) => setForm({ ...form, entry_status: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-slate-900 dark:text-[#E6EDF3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40 focus:border-[#2E75B6] transition-colors"
              >
                <option value="active">Actief</option>
                <option value="pending">In Behandeling</option>
                <option value="archived">Gearchiveerd</option>
              </select>
            </div>
          )}

          {/* Visibility */}
          <VisibilitySelector
            value={form.visibility}
            onChange={(v) => setForm({ ...form, visibility: v, ...(v === 'normal' ? { visibility_granted_users: [] } : {}) })}
            grantedUsers={form.visibility_granted_users}
            onGrantedUsersChange={(users) => setForm({ ...form, visibility_granted_users: users })}
            userRole={user?.role}
            allUsers={allUsers}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 justify-end">
          <button
            type="button"
            onClick={() => navigate(isEditing ? `/kennisbank/${entryId}` : '/kennisbank')}
            className="px-4 py-2.5 rounded-lg border border-slate-200 dark:border-[#30363D] text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#21262D] transition-colors"
          >
            Annuleren
          </button>
          <button
            type="submit"
            data-testid="entry-save-button"
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50 shadow-sm"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Opslaan...' : (isEditing ? 'Bijwerken' : 'Opslaan')}
          </button>
        </div>
      </form>
    </div>
  );
}
